/**
 * T-047: dashboard cache status + flush actions.
 *
 * Self-flush is admin-tier (every authenticated admin can flush their
 * own cache). Global flush is superadmin-only and surfaces a
 * confirmation dialog.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  getDashboardCacheStatus,
  flushDashboardCache,
  updateDashboardCacheConfig,
} from '@/lib/pocketSettingsApi';
import type {
  PocketCacheFlushScope,
  PocketDashboardCacheStatus,
} from '@/types/pocketSettings';

export function DashboardCacheCard() {
  const { isSuperadmin } = useRBAC();
  const [status, setStatus] = useState<PocketDashboardCacheStatus | null>(null);
  const [pending, setPending] = useState<PocketCacheFlushScope | null>(null);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // T-047 retrofit: editable TTL/SWR (superadmin only).
  const [editing, setEditing] = useState(false);
  const [draftTtl, setDraftTtl] = useState('');
  const [draftSwr, setDraftSwr] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getDashboardCacheStatus()
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load cache status.");
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function startEdit() {
    if (!status) return;
    setDraftTtl(String(status.ttl_seconds));
    setDraftSwr(String(status.swr_seconds));
    setEditing(true);
  }

  async function saveEdit() {
    const ttl = Number(draftTtl);
    const swr = Number(draftSwr);
    if (
      !Number.isInteger(ttl) ||
      ttl < 5 ||
      ttl > 1800 ||
      !Number.isInteger(swr) ||
      swr < 1 ||
      swr >= ttl
    ) {
      toast.error('TTL must be 5-1800s. SWR must be 1 to (TTL-1).');
      return;
    }
    setSaving(true);
    try {
      await updateDashboardCacheConfig({ ttl_seconds: ttl, swr_seconds: swr });
      toast.success('Cache config saved.');
      setEditing(false);
      refresh();
    } catch {
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleFlush(scope: PocketCacheFlushScope) {
    setPending(scope);
    try {
      const result = await flushDashboardCache(scope);
      toast.success(
        `Flushed ${result.flushed} ${scope === 'all' ? 'global' : 'personal'} cache entr${result.flushed === 1 ? 'y' : 'ies'}.`,
      );
      setConfirmAllOpen(false);
      refresh();
    } catch {
      toast.error('Flush failed.');
    } finally {
      setPending(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Dashboard cache</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Snapshot reuse for the iPad dashboard. <span className="text-foreground font-medium">TTL</span> = how long
          a snapshot lives. <span className="text-foreground font-medium">SWR</span> = age past which it's served
          stale while we refresh in the background. Lower = fresher, more DB load.
        </p>
        {error ? <p className="text-sm text-destructive mb-4">{error}</p> : null}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          {editing ? (
            <>
              <div className="rounded-md border border-border/50 p-4 bg-card">
                <div className="text-xs text-muted-foreground mb-1">TTL (seconds)</div>
                <Input
                  type="number"
                  min={5}
                  max={1800}
                  value={draftTtl}
                  onChange={(e) => setDraftTtl(e.target.value)}
                  aria-label="ttl_seconds"
                />
              </div>
              <div className="rounded-md border border-border/50 p-4 bg-card">
                <div className="text-xs text-muted-foreground mb-1">SWR (seconds)</div>
                <Input
                  type="number"
                  min={1}
                  max={1799}
                  value={draftSwr}
                  onChange={(e) => setDraftSwr(e.target.value)}
                  aria-label="swr_seconds"
                />
              </div>
              <Tile
                label="Cached entries"
                value={status ? String(status.cached_key_count) : '—'}
              />
            </>
          ) : (
            <>
              <Tile
                label="TTL"
                value={status ? `${status.ttl_seconds}s` : '—'}
                badge={status?.is_default === false ? 'override' : undefined}
              />
              <Tile
                label="SWR threshold"
                value={status ? `${status.swr_seconds}s` : '—'}
              />
              <Tile
                label="Cached entries"
                value={status ? String(status.cached_key_count) : '—'}
              />
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {editing ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              {isSuperadmin ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={startEdit}
                  disabled={pending !== null || !status}
                >
                  Edit TTL / SWR
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleFlush('self')}
                disabled={pending !== null}
              >
                {pending === 'self' ? 'Flushing…' : 'Flush my cache'}
              </Button>
              {isSuperadmin ? (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setConfirmAllOpen(true)}
                  disabled={pending !== null}
                >
                  Flush all
                </Button>
              ) : null}
            </>
          )}
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          <strong>What "Flush my cache" does:</strong> clears your own dashboard
          snapshot. Useful right after you change something and want to confirm
          the new data shows up on Pocket without waiting the TTL.{' '}
          <strong>What "Flush all" does:</strong> clears everyone's snapshot;
          every iPad's next dashboard load will hit the database. Save TTL/SWR
          edits and they take effect within 60 seconds (every change is audited).
        </p>
      </CardContent>

      <Dialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Flush all dashboard caches?</DialogTitle>
            <DialogDescription>
              This deletes every <span className="font-mono">pocket-dashboard:*</span> entry
              across all users. Each user&apos;s next dashboard load will hit the database
              once before re-warming the cache.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAllOpen(false)}
              disabled={pending !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleFlush('all')}
              disabled={pending !== null}
            >
              {pending === 'all' ? 'Flushing…' : 'Flush all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Tile({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: string;
}) {
  return (
    <div className="rounded-md border border-border/50 p-4 bg-card">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">{label}</div>
        {badge ? (
          <Badge variant="secondary" className="text-xs">
            {badge}
          </Badge>
        ) : null}
      </div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}
